import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileUp, Loader2, Sparkles, X } from 'lucide-react';
import {
  AI_SOURCE_FORMATS,
  AI_SOURCE_MAX_BYTES,
  type AiDraft,
  type AiJobRequest,
} from '@ready/shared';
import { useApi, type ContentOwner } from '@/lib/api';
import { formatBytes } from '@/lib/files';
import { Button, ErrorText, Field, Textarea } from '@/components/ui';
import { useCourseFiles } from '@/components/course/file-picker';

/*
 * Shared pieces of AI assist: whether it's on, running a draft job (start,
 * then poll until it finishes), source material fields and the panel frame.
 */

export function useAiEnabled(): boolean {
  const api = useApi();
  const status = useQuery({
    queryKey: ['aiStatus'],
    queryFn: () => api.aiStatus(),
    staleTime: 10 * 60_000,
  });
  return status.data?.enabled ?? false;
}

/** Starts a draft and polls it every two seconds until it succeeds or fails. */
export function useAiDraft<K extends AiDraft['kind']>(kind: K) {
  const api = useApi();
  const [jobId, setJobId] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: (input: AiJobRequest) => api.startAiJob(input),
    onSuccess: (job) => setJobId(job.id),
  });
  const job = useQuery({
    queryKey: ['aiJob', jobId],
    queryFn: () => api.aiJob(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (q) =>
      q.state.data?.status === 'succeeded' || q.state.data?.status === 'failed' ? false : 2_000,
  });
  const status = job.data?.status;
  const draft =
    status === 'succeeded' && job.data?.result?.kind === kind
      ? (job.data.result as Extract<AiDraft, { kind: K }>)
      : null;
  return {
    start: (input: AiJobRequest) => {
      setJobId(null);
      start.mutate(input);
    },
    reset: () => {
      setJobId(null);
      start.reset();
    },
    drafting: start.isPending || (Boolean(jobId) && !draft && status !== 'failed'),
    draft,
    error:
      start.error ?? (status === 'failed' ? new Error(job.data?.error ?? 'Drafting failed') : null),
  };
}

/** Elapsed seconds while drafting, so a long wait doesn't look stuck. */
function useElapsed(active: boolean) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) return setSeconds(0);
    const t = setInterval(() => setSeconds((s) => s + 1), 1_000);
    return () => clearInterval(t);
  }, [active]);
  return seconds;
}

export function DraftingNote({ active }: { active: boolean }) {
  const seconds = useElapsed(active);
  if (!active) return null;
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      Drafting… usually 20 to 60 seconds{seconds >= 5 ? ` (${seconds}s)` : ''}. You can keep
      working; leave this panel open.
    </p>
  );
}

export function AiButton({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <Button variant="secondary" {...props}>
      <Sparkles className="size-4 text-primary" aria-hidden />
      {children}
    </Button>
  );
}

/** Frame for an AI assist panel, with a close button. */
export function AiPanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-primary/40 bg-card p-4 sm:p-5">
      <div className="mb-4 flex items-start gap-2">
        <Sparkles className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <h2 className="min-w-0 flex-1 font-semibold">{title}</h2>
        <button
          type="button"
          className="-m-2 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="space-y-4">{children}</div>
      <p className="mt-4 text-xs text-muted-foreground">
        Drafts come from Claude on Amazon Bedrock. Check facts before publishing; nothing is saved
        until you add it.
      </p>
    </section>
  );
}

export interface SourceValue {
  sourceText: string;
  sourceAssetIds: string[];
}
export const emptySource: SourceValue = { sourceText: '', sourceAssetIds: [] };

/** For the job request: blank fields left out. */
export const sourceInput = (s: SourceValue) => ({
  ...(s.sourceText.trim() ? { sourceText: s.sourceText.trim() } : {}),
  sourceAssetIds: s.sourceAssetIds,
});

const extension = (name: string) => name.toLowerCase().split('.').pop() ?? '';

/** Optional material to draft from: pasted or imported text, and up to three course files. */
export function SourceFields({
  id,
  owner,
  value,
  onChange,
}: {
  id: string;
  owner: ContentOwner;
  value: SourceValue;
  onChange: (v: SourceValue) => void;
}) {
  const files = useCourseFiles(owner);
  const input = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const usable = (files.data?.files ?? []).filter(
    (f) =>
      f.status === 'ready' &&
      AI_SOURCE_FORMATS.includes(extension(f.filename)) &&
      f.sizeBytes <= AI_SOURCE_MAX_BYTES,
  );
  const toggle = (fileId: string) =>
    onChange({
      ...value,
      sourceAssetIds: value.sourceAssetIds.includes(fileId)
        ? value.sourceAssetIds.filter((x) => x !== fileId)
        : [...value.sourceAssetIds, fileId].slice(-3),
    });
  const importText = async (file: File) => {
    setImportError(null);
    if (file.size > 500_000) return setImportError('Choose a text file under 500 KB.');
    const text = await file.text();
    onChange({ ...value, sourceText: [value.sourceText, text].filter(Boolean).join('\n\n') });
  };
  return (
    <details
      className="rounded-lg border border-border p-3"
      open={Boolean(value.sourceText || value.sourceAssetIds.length)}
    >
      <summary className="cursor-pointer text-sm font-medium">
        Source material (optional)
        {value.sourceAssetIds.length + (value.sourceText ? 1 : 0) > 0 && (
          <span className="ml-2 text-xs text-muted-foreground">
            {value.sourceAssetIds.length} file{value.sourceAssetIds.length === 1 ? '' : 's'}
            {value.sourceText ? ', text' : ''}
          </span>
        )}
      </summary>
      <div className="mt-3 space-y-3">
        <Field
          label="Notes, transcript or existing material"
          htmlFor={`${id}-source`}
          hint="The draft follows this material. Up to 60,000 characters."
        >
          <Textarea
            id={`${id}-source`}
            rows={5}
            maxLength={60_000}
            value={value.sourceText}
            onChange={(e) => onChange({ ...value, sourceText: e.target.value })}
          />
        </Field>
        <div>
          <Button type="button" variant="ghost" onClick={() => input.current?.click()}>
            <FileUp className="size-4" aria-hidden />
            Import a Markdown or text file
          </Button>
          <input
            ref={input}
            type="file"
            accept=".md,.mdx,.txt,text/markdown,text/plain"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importText(f);
              e.target.value = '';
            }}
          />
          <ErrorText error={importError} />
        </div>
        {usable.length > 0 && (
          <fieldset>
            <legend className="text-sm font-medium">Course files (up to 3)</legend>
            <p className="text-xs text-muted-foreground">
              PDF, Word, Excel, CSV, HTML, Markdown or text, up to 4.5 MB each.
            </p>
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
              {usable.map((f) => (
                <li key={f.id}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md p-2 text-sm hover:bg-muted">
                    <input
                      type="checkbox"
                      className="size-5 shrink-0 accent-primary"
                      checked={value.sourceAssetIds.includes(f.id)}
                      onChange={() => toggle(f.id)}
                    />
                    <span className="min-w-0 flex-1 truncate">{f.filename}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatBytes(f.sizeBytes)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
      </div>
    </details>
  );
}
